import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Building2, X, CheckCircle2, Clock } from 'lucide-react';
import { API, axios } from '../../utils/api';
import { leggiToken } from '../../utils/sessione';

const CreateAgencyDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: '',
    contatto: '',
    telefono: '',
    email: '',
    sito_web: '',
    comune: '',
    indirizzo: '',
    stato_verifica: 'in_attesa',
    stato_dichiarato: 'in_attesa_dichiarazione',
    note_admin: '',
    logo_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleUploadLogo = async (file, agenziaId) => {
    if (!file) return null;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG, WEBP, GIF o SVG');
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande (max 10MB)');
      return null;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = leggiToken();
      const res = await axios.post(`${API}/agenzie/${agenziaId}/upload-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      return res.data.logo_url;
    } catch (error) {
      console.error('Errore upload logo:', error);
      toast.error("Errore nel caricamento del logo");
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLocalFile = (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG, WEBP, GIF o SVG');
      return;
    }
    // Mostra anteprima locale con object URL
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({...prev, logo_url: previewUrl, _logoFile: file}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error("Il nome dell'agenzia è obbligatorio");
      return;
    }

    try {
      setLoading(true);
      let submitData = {...formData};
      delete submitData._logoFile;
      // Sincronizza si_puo_lavorare con stato_verifica
      submitData.si_puo_lavorare = submitData.stato_verifica === 'lavorabile';
      // Se c'è un URL blob locale, non inviarlo — lo caricheremo dopo
      if (submitData.logo_url && submitData.logo_url.startsWith('blob:')) {
        delete submitData.logo_url;
      }

      if (formData.comune.trim()) {
        try {
          const geoRes = await axios.get(`${API}/geocode?q=${encodeURIComponent(formData.comune)}`);
          if (geoRes.data.lat) {
            submitData.latitudine = geoRes.data.lat;
            submitData.longitudine = geoRes.data.lon;
          }
        } catch (e) { /* ignore geocode errors */ }
      }

      const response = await axios.post(`${API}/agenzie`, submitData);
      const newAgenziaId = response.data.id;

      // Upload logo file se presente
      if (formData._logoFile && newAgenziaId) {
        await handleUploadLogo(formData._logoFile, newAgenziaId);
      }
      // Oppure salva URL diretto se inserito manualmente
      else if (formData.logo_url && !formData.logo_url.startsWith('blob:') && formData.logo_url.trim()) {
        await axios.put(`${API}/agenzie/${newAgenziaId}`, { logo_url: formData.logo_url.trim() });
      }

      toast.success('Agenzia creata con successo');
      setFormData({
        nome: '', contatto: '', telefono: '', email: '', sito_web: '',
        comune: '', indirizzo: '', stato_verifica: 'in_attesa', stato_dichiarato: 'in_attesa_dichiarazione', note_admin: '', logo_url: ''
      });
      onSuccess();
    } catch (error) {
      console.error('Errore creazione agenzia:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const logoPreview = formData.logo_url && formData.logo_url.trim();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crea Nuova Agenzia</DialogTitle>
          <DialogDescription>
            Inserisci i dettagli della nuova agenzia immobiliare
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Logo Upload */}
          <div>
            <Label>Logo Agenzia</Label>
            <div className="mt-1 flex items-start gap-4">
              {/* Anteprima logo */}
              <div className="shrink-0 w-20 h-20 rounded-xl border-2 border-gold/30 overflow-hidden bg-background flex items-center justify-center">
                {logoPreview ? (
                  <img src={formData.logo_url} alt="Logo anteprima" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <Building2 className="h-8 w-8 text-gold/40" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                {/* Drag and drop */}
                <div
                  data-testid="logo-dropzone"
                  className={`border-2 border-dashed rounded-lg p-3 text-center transition-all cursor-pointer ${
                    isDragging ? 'border-gold bg-gold/10' : 'border-gold/30 hover:border-gold/60 hover:bg-gold/5'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file) handleLocalFile(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleLocalFile(file);
                      e.target.value = '';
                    }}
                  />
                  <Upload className="h-5 w-5 mx-auto mb-1 text-gold/60" />
                  <p className="text-xs text-white/80">
                    {isDragging ? 'Rilascia qui il logo' : 'Trascina il logo o clicca per sfogliare'}
                  </p>
                  <p className="text-xs text-muted-foreground">JPG, PNG, WEBP, GIF, SVG (max 10MB)</p>
                </div>
                {/* Oppure URL */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-gold/20" />
                  <span className="text-xs text-muted-foreground">oppure incolla URL</span>
                  <div className="flex-1 h-px bg-gold/20" />
                </div>
                <Input
                  data-testid="logo-url-input"
                  placeholder="https://esempio.com/logo.png"
                  value={formData._logoFile ? '' : formData.logo_url}
                  onChange={(e) => setFormData(prev => ({...prev, logo_url: e.target.value, _logoFile: undefined}))}
                />
                {logoPreview && (
                  <Button type="button" variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => setFormData(prev => ({...prev, logo_url: '', _logoFile: undefined}))}>
                    <X className="h-3 w-3 mr-1" /> Rimuovi logo
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="nome">Nome Agenzia *</Label>
            <Input
              id="nome"
              data-testid="agency-name-input"
              value={formData.nome}
              onChange={(e) => setFormData({...formData, nome: e.target.value})}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contatto">Contatto</Label>
              <Input
                id="contatto"
                value={formData.contatto}
                onChange={(e) => setFormData({...formData, contatto: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="telefono">Telefono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>

          <div>
            <Label htmlFor="sito_web">Sito Web</Label>
            <Input
              id="sito_web"
              type="url"
              value={formData.sito_web}
              onChange={(e) => setFormData({...formData, sito_web: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="comune">Comune *</Label>
              <Input
                id="comune"
                placeholder="es. Milano, Monza, Seregno..."
                value={formData.comune}
                onChange={(e) => setFormData({...formData, comune: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="indirizzo">Indirizzo</Label>
              <Input
                id="indirizzo"
                placeholder="Via, numero civico..."
                value={formData.indirizzo}
                onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
              />
            </div>
          </div>

          {/* Stato Collaborazione - 3 opzioni */}
          <div>
            <Label>Stato Collaborazione</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_verifica: 'lavorabile'})}
                className={`flex-1 h-9 ${formData.stato_verifica === 'lavorabile' ? 'bg-green-500/20 text-green-400 ring-2 ring-green-500/50' : 'text-muted-foreground hover:text-green-400 hover:bg-green-500/10'}`}
                data-testid="stato-lavorabile"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Lavorabile
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_verifica: 'in_attesa'})}
                className={`flex-1 h-9 ${formData.stato_verifica === 'in_attesa' ? 'bg-gold/20 text-gold ring-2 ring-gold/50' : 'text-muted-foreground hover:text-gold hover:bg-gold/10'}`}
                data-testid="stato-in-attesa"
              >
                <Clock className="h-4 w-4 mr-1.5" /> In Attesa
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_verifica: 'non_lavorabile'})}
                className={`flex-1 h-9 ${formData.stato_verifica === 'non_lavorabile' ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50' : 'text-muted-foreground hover:text-red-400 hover:bg-red-500/10'}`}
                data-testid="stato-non-lavorabile"
              >
                <X className="h-4 w-4 mr-1.5" /> Non Lavorabile
              </Button>
            </div>
          </div>

          {/* Stato Dichiarato */}
          <div>
            <Label>Dichiarato</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_dichiarato: 'dichiarato'})}
                className={`flex-1 h-9 ${formData.stato_dichiarato === 'dichiarato' ? 'bg-blue-500/20 text-blue-400 ring-2 ring-blue-500/50' : 'text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10'}`}
                data-testid="stato-dichiarato"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Dichiarato
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_dichiarato: 'in_attesa_dichiarazione'})}
                className={`flex-1 h-9 ${formData.stato_dichiarato === 'in_attesa_dichiarazione' ? 'bg-gold/20 text-gold ring-2 ring-gold/50' : 'text-muted-foreground hover:text-gold hover:bg-gold/10'}`}
                data-testid="stato-in-attesa-dichiarazione"
              >
                <Clock className="h-4 w-4 mr-1.5" /> In Attesa
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFormData({...formData, stato_dichiarato: 'non_dichiarato'})}
                className={`flex-1 h-9 ${formData.stato_dichiarato === 'non_dichiarato' ? 'bg-orange-500/20 text-orange-400 ring-2 ring-orange-500/50' : 'text-muted-foreground hover:text-orange-400 hover:bg-orange-500/10'}`}
                data-testid="stato-non-dichiarato"
              >
                <X className="h-4 w-4 mr-1.5" /> Non Dichiarato
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="note_admin">Note Admin</Label>
            <Textarea
              id="note_admin"
              value={formData.note_admin}
              onChange={(e) => setFormData({...formData, note_admin: e.target.value})}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={loading || uploadingLogo} data-testid="create-agency-submit">
              {loading ? 'Creazione...' : 'Crea Agenzia'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { CreateAgencyDialog };
